import { useState } from "react"
import { useNavigate } from "react-router"
import { useTranslation } from "react-i18next"

import { ellipsis } from "@f2e/shared/utils"

import { useDeleteWorkspacesApi, useLeaveWorkspaceApi } from "@/hooks/api"
import { useToast } from "@/hooks"
import { useStore } from "@/store"
import { Workspace } from "@/types"
import { AlertBox } from "@/components"

export interface UseWorkspaceActionsOptions {
  workspace?: Workspace
  organizationId: string
  workspaceId?: string
  onDeleteSuccess?: (workspaceId: string) => void
  onLeaveSuccess?: (workspaceId: string) => void
  showSuccessToast?: boolean
  navigateOnSuccess?: boolean
  maxNameLength?: number
}

export const useWorkspaceActions = ({
  workspace,
  organizationId,
  workspaceId,
  onDeleteSuccess,
  onLeaveSuccess,
  showSuccessToast = false,
  navigateOnSuccess = false,
  maxNameLength,
}: UseWorkspaceActionsOptions) => {
  const navigate = useNavigate()
  const { t } = useTranslation()
  const { alertToast, successToast } = useToast()
  const setWorkspaces = useStore((state) => state.setWorkspaces)

  const [showDeleteDialog, setShowDeleteDialog] = useState(false)
  const [showLeaveDialog, setShowLeaveDialog] = useState(false)

  const targetWorkspaceId = workspaceId || workspace?.id || ""

  const {
    error: deleteError,
    data: deleteRespData,
    fetchFn: deleteFn,
  } = useDeleteWorkspacesApi(organizationId, targetWorkspaceId)

  const {
    error: leaveError,
    data: leaveRespData,
    fetchFn: leaveFn,
  } = useLeaveWorkspaceApi(organizationId, targetWorkspaceId)

  const handleDeleteClick = (e?: React.MouseEvent) => {
    e?.stopPropagation()
    setShowDeleteDialog(true)
  }

  const handleLeaveClick = (e?: React.MouseEvent) => {
    e?.stopPropagation()
    setShowLeaveDialog(true)
  }

  const handleDeleteConfirm = () => {
    if (!targetWorkspaceId) return

    deleteFn({
      callbackFn: (resp) => {
        if (!resp) return

        setWorkspaces((prev) => prev.filter((w) => w.id !== targetWorkspaceId))

        if (showSuccessToast && workspace) {
          successToast({
            title: t("workspace.message.deleted"),
            description: t("workspace.message.deletedDesc", {
              name: workspace.name,
            }),
          })
        }

        if (navigateOnSuccess) {
          navigate("/workspaces")
        }

        setShowDeleteDialog(false)
        onDeleteSuccess?.(targetWorkspaceId)
      },
    })
  }

  const handleLeaveConfirm = () => {
    if (!targetWorkspaceId) return

    leaveFn({
      callbackFn: (resp) => {
        if (!resp) return

        setWorkspaces((prev) => prev.filter((w) => w.id !== targetWorkspaceId))

        if (showSuccessToast && workspace) {
          successToast({
            title: t("workspace.message.left"),
            description: t("workspace.message.leftDesc", {
              name: workspace.name,
            }),
          })
        }

        if (navigateOnSuccess) {
          navigate("/workspaces")
        }

        setShowLeaveDialog(false)
        onLeaveSuccess?.(targetWorkspaceId)
      },
    })
  }

  const renderAlerts = () => {
    if (!workspace) return null

    const displayName = maxNameLength
      ? ellipsis(workspace.name, maxNameLength)
      : workspace.name

    return (
      <>
        <AlertBox
          title={t("workspace.actionButton.alerts.delete.title")}
          description={t("workspace.actionButton.alerts.delete.description", {
            name: displayName,
          })}
          cancelText={t("workspace.cancel")}
          confirmText={t("workspace.delete")}
          open={showDeleteDialog}
          onOpenChange={setShowDeleteDialog}
          onConfirm={handleDeleteConfirm}
        />

        <AlertBox
          title={t("workspace.actionButton.alerts.leave.title")}
          description={t("workspace.actionButton.alerts.leave.description", {
            name: displayName,
          })}
          cancelText={t("workspace.cancel")}
          confirmText={t("workspace.leave")}
          open={showLeaveDialog}
          onOpenChange={setShowLeaveDialog}
          onConfirm={handleLeaveConfirm}
        />
      </>
    )
  }

  return {
    showDeleteDialog,
    showLeaveDialog,
    setShowDeleteDialog,
    setShowLeaveDialog,
    handleDeleteClick,
    handleLeaveClick,
    handleDeleteConfirm,
    handleLeaveConfirm,
    deleteError,
    deleteRespData,
    leaveError,
    leaveRespData,
    alertToast,
    renderAlerts,
  }
}
